import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ScrollView,
} from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ParentStackParamList } from "../../navigation/types";
import { childrenApi } from "../../api/children";
import { useAuthStore } from "../../state/authStore";
import { Mascot } from "../../components/Mascot";
import { SelectField } from "../../components/SelectField";
import { colors, fontFamily, mascotColors, radii } from "../../theme";
import { CONTENT_CATEGORIES } from "../../data/categories";
import {
  MONTH_LABELS,
  ageFromBirthday,
  birthYearOptions,
} from "../../utils/birthday";

type Props = NativeStackScreenProps<ParentStackParamList, "AddChild">;

// One shared vocabulary with Controls — see src/data/categories.ts. The old
// list offered Animals/Music/Stories, which Controls had no chip for, so an
// interest chosen here could never be switched off again.
const INTERESTS = CONTENT_CATEGORIES;


export default function AddChildScreen({ navigation, route }: Props) {
  const params = route.params ?? {};
  const fromOnboarding = params.fromOnboarding;
  // Seeds carried over from Signup's child fields (optional).
  const seedName = params.seedName;
  const seedAge = params.seedAge;
  const seedColor = params.seedColor;

  const currentYear = new Date().getFullYear();
  const YEARS = birthYearOptions();

  // No defaults — a fresh profile starts blank (no seed → empty name).
  const [name, setName] = useState(seedName || "");
  // No default birthday — the parent picks a month + year (nothing pre-selected).
  const [birthMonth, setBirthMonth] = useState<number | null>(null);
  // Derive the seed birth year from the seeded age when present (signup flow).
  const [birthYear, setBirthYear] = useState<number | null>(
    seedAge ? currentYear - seedAge : null,
  );
  const [color, setColor] = useState<string>(seedColor || mascotColors[0]);

  const birthday =
    birthMonth !== null && birthYear !== null
      ? new Date(birthYear, birthMonth, 1)
      : null;
  const age = birthday ? ageFromBirthday(birthday) : null;
  // No pre-selected interests — the parent chooses; saved to Controls.categories.
  const [picked, setPicked] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const { accessToken } = useAuthStore();

  const toggleInterest = (i: string) =>
    setPicked((cur) =>
      cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i],
    );

  const handleCreate = async () => {
    if (!name.trim()) {
      Alert.alert("Please enter a name");
      return;
    }
    if (!birthday) {
      Alert.alert("Please pick a birthday (month and year)");
      return;
    }
    setLoading(true);
    try {
      const created = await childrenApi.create(
        {
          name: name.trim(),
          age: ageFromBirthday(birthday),
          birthday: birthday.toISOString(),
          color,
        },
        accessToken!,
      );
      // Persist chosen interests to the child's parental Controls — the
      // recommendations feed filters the kid's videos by Controls.categories,
      // so lowercase them to match the content slugs (numbers/letters/shapes/…).
      // Best-effort: the child already exists, so a hiccup here isn't fatal.
      if (picked.length) {
        try {
          await childrenApi.updateControls(
            created.id,
            // Canonical casing — see the note on ControlsScreen.toggleCat.
            { categories: picked },
            accessToken!,
          );
        } catch {
          // Non-fatal — interests didn't save; the profile is still created.
        }
      }
      if (fromOnboarding) {
        navigation.replace("PinSetup");
      } else {
        // Always land in the parent dashboard so the freshly-created kid
        // shows up immediately. The previous `goBack()` returned to
        // whatever pushed AddChild — when that was ProfileSelect (the new
        // "Add child" empty-state flow), the picker doesn't re-fetch on
        // focus and the kid appears to vanish until reload. ParentHome
        // refetches on every focus and is the natural home for managing
        // children anyway.
        navigation.navigate("ParentHome");
      }
    } catch (e: unknown) {
      Alert.alert("Failed to create", e instanceof Error ? e.message : "Try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backBtn}
          activeOpacity={0.7}
        >
          <Text style={styles.backChevron}>‹</Text>
        </TouchableOpacity>
        <View>
          <Text style={styles.eyebrow}>NEW PROFILE</Text>
          <Text style={styles.headline}>Add a child</Text>
        </View>
      </View>

      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}
      >
        {/* Preview card */}
        <View style={styles.previewCard}>
          <View style={styles.previewMascotRing}>
            <View style={styles.previewMascotInner}>
              <Mascot mood="happy" color={color} size={94} />
            </View>
          </View>
          <Text style={styles.previewName}>{name || "Child"}</Text>
          <Text style={styles.previewAge}>
            {age !== null ? `${age} years` : "Age not set"}
          </Text>
        </View>

        {/* Name */}
        <Field label="NAME">
          <TextInput
            value={name}
            onChangeText={setName}
            placeholder="Child's name"
            placeholderTextColor={colors.ink3}
            style={styles.input}
          />
        </Field>

        {/* Birthday: month + year dropdown selects. Day-level granularity is
            overkill for age-banded recommendations; month + year keeps it
            accurate across the year without a sprawling chip grid. */}
        <Field label="BIRTHDAY (REQUIRED)">
          <View style={styles.selectRow}>
            <View style={styles.selectHalf}>
              <SelectField
                placeholder="Month"
                value={birthMonth !== null ? MONTH_LABELS[birthMonth] : null}
                options={MONTH_LABELS.map((m, i) => ({ label: m, value: i }))}
                onSelect={setBirthMonth}
              />
            </View>
            <View style={styles.selectHalf}>
              <SelectField
                placeholder="Year"
                value={birthYear !== null ? String(birthYear) : null}
                options={YEARS.map((y) => ({ label: String(y), value: y }))}
                onSelect={setBirthYear}
              />
            </View>
          </View>
        </Field>

        {/* Mascot color */}
        <Field label="MASCOT COLOR">
          <View style={styles.colorRow}>
            {mascotColors.map((c) => {
              const active = c === color;
              return (
                <TouchableOpacity
                  key={c}
                  onPress={() => setColor(c)}
                  activeOpacity={0.8}
                  style={[
                    styles.colorSwatch,
                    {
                      backgroundColor: c,
                      borderColor: active ? colors.ink : "transparent",
                    },
                  ]}
                />
              );
            })}
          </View>
        </Field>

        {/* Interests */}
        <Field label="WHAT DO THEY LOVE?">
          <View style={styles.interestRow}>
            {INTERESTS.map((i) => {
              const on = picked.includes(i);
              return (
                <TouchableOpacity
                  key={i}
                  onPress={() => toggleInterest(i)}
                  activeOpacity={0.7}
                  style={[
                    styles.interestChip,
                    {
                      backgroundColor: on ? colors.ink : "transparent",
                      borderColor: on ? colors.ink : colors.lineStrong,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.interestText,
                      { color: on ? "#fff" : colors.ink2 },
                    ]}
                  >
                    {i}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </Field>
      </ScrollView>

      {/* Sticky CTA */}
      <View style={styles.cta}>
        <TouchableOpacity
          style={styles.createBtn}
          onPress={handleCreate}
          disabled={loading}
          activeOpacity={0.85}
        >
          <Text style={styles.createBtnText}>
            {loading ? "Creating…" : "Create profile"}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View style={fieldStyles.wrap}>
      <Text style={fieldStyles.label}>{label}</Text>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  // Header
  header: {
    paddingTop: 56,
    paddingHorizontal: 20,
    paddingBottom: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  backBtn: {
    width: 40,
    height: 40,
    borderRadius: radii.sm,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.line,
    alignItems: "center",
    justifyContent: "center",
  },
  backChevron: {
    fontFamily,
    fontSize: 24,
    fontWeight: "700",
    color: colors.ink,
    lineHeight: 26,
    marginTop: -2,
  },
  eyebrow: {
    fontFamily,
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 1.4,
    color: colors.ink2,
  },
  headline: {
    fontFamily,
    fontSize: 22,
    fontWeight: "900",
    color: colors.ink,
    letterSpacing: -0.2,
  },

  // Body
  scroll: {
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 24,
  },

  // Preview card
  previewCard: {
    backgroundColor: colors.bgWarm,
    borderRadius: radii.xl,
    paddingVertical: 20,
    alignItems: "center",
    marginBottom: 18,
  },
  previewMascotRing: {
    width: 110,
    height: 110,
    borderRadius: 55,
    backgroundColor: colors.surface,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  previewMascotInner: {
    marginTop: 14,
  },
  previewName: {
    fontFamily,
    fontSize: 22,
    fontWeight: "900",
    color: colors.ink,
    marginTop: 8,
  },
  previewAge: {
    fontFamily,
    fontSize: 13,
    fontWeight: "700",
    color: colors.ink2,
    marginTop: 2,
  },

  // Name input
  input: {
    height: 44,
    paddingHorizontal: 14,
    borderWidth: 1.5,
    borderColor: colors.lineStrong,
    borderRadius: radii.md - 2,
    backgroundColor: colors.surface,
    color: colors.ink,
    fontFamily,
    fontSize: 16,
    fontWeight: "700",
  },

  // Birthday dropdown selects (month + year)
  selectRow: {
    flexDirection: "row",
    gap: 12,
  },
  selectHalf: {
    flex: 1,
  },

  // Age chips
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  ageChip: {
    minWidth: 44,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: radii.pill,
    borderWidth: 1.5,
    alignItems: "center",
    justifyContent: "center",
  },
  ageChipText: {
    fontFamily,
    fontSize: 14,
    fontWeight: "800",
  },

  // Color swatches
  colorRow: {
    flexDirection: "row",
    gap: 12,
  },
  colorSwatch: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 3,
  },

  // Interests
  interestRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  interestChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: radii.pill,
    borderWidth: 1.5,
  },
  interestText: {
    fontFamily,
    fontSize: 13,
    fontWeight: "800",
  },

  // CTA
  cta: {
    paddingHorizontal: 20,
    paddingBottom: 32,
    paddingTop: 8,
  },
  createBtn: {
    backgroundColor: colors.primary,
    borderRadius: radii.md,
    paddingVertical: 16,
    alignItems: "center",
    borderBottomWidth: 3,
    borderBottomColor: "rgba(0,0,0,0.12)",
  },
  createBtnText: {
    fontFamily,
    color: "#fff",
    fontSize: 16,
    fontWeight: "800",
  },
});

const fieldStyles = StyleSheet.create({
  wrap: {
    marginBottom: 16,
  },
  label: {
    fontFamily,
    fontSize: 11,
    fontWeight: "800",
    color: colors.ink2,
    letterSpacing: 1.4,
    marginBottom: 8,
  },
});
